import React, {useEffect, useState} from "react";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Select, {SelectOption} from "@amzn/meridian/select";
import DatePicker from "@amzn/meridian/date-picker";
import Alert from "@amzn/meridian/alert";
import Loader from "@amzn/meridian/loader";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Icon from "@amzn/meridian/icon";
import plusTokens from "@amzn/meridian-tokens/base/icon/plus";
import closeTokens from "@amzn/meridian-tokens/base/icon/close-large";
import {colorGray700} from "@amzn/meridian-tokens/base/color";
import {FormattedMessage, injectIntl} from "react-intl";

import Box from "@amzn/meridian/box";
import ContainerCard from "./ContainerCard";
import ContainerDetailsModal from "./ContainerDetailsModal";
import CreateContainerModal from "./CreateContainerModal";
import CreateItemModal from "./CreateItemModal";
import {printContainerLabel} from "./printContainerLabel";
import {searchEWasteContainers} from "../../commons/api/SearchEWasteContainers";
import {closeEWasteContainer as closeContainerApi} from "../../commons/api/CloseEWasteContainer";
import {getStationName, buildTitle, localStartOfDayIso, localEndOfDayIso} from "../../util/CommonUtils";
import {WTN_MSG} from "../../commons/constants/WtnMessageIds";
import {FILTER_TYPE, QUERY_TYPE, QUERY_FIELD} from "../../commons/constants/WadmQueryFilter";

const STATUS_FILTER = {
    ALL: "ALL",
    OPEN: "OPEN",
    CLOSED: "CLOSED"
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

function getOneMonthAgo() {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
}

const INITIAL_FILTERS = {
    status: STATUS_FILTER.OPEN,
    createdBefore: "",
    createdAfter: getOneMonthAgo()
};

/**
 * Builds the WADM-compatible queryFilters array from the UI filter state.
 */
function buildQueryFilters({status, createdAfter, createdBefore}) {
    const queryFilters = [];

    if (status && status !== STATUS_FILTER.ALL) {
        queryFilters.push({
            filterType: FILTER_TYPE.MUST,
            queryParams: [{
                field: QUERY_FIELD.STATUS,
                queryType: QUERY_TYPE.MATCH,
                value: [status]
            }]
        });
    }

    if (createdAfter) {
        queryFilters.push({
            filterType: FILTER_TYPE.MUST,
            queryParams: [{
                field: QUERY_FIELD.CREATED_AT,
                queryType: QUERY_TYPE.GREATER_THAN_EQUAL,
                value: [localStartOfDayIso(createdAfter)]
            }]
        });
    }

    if (createdBefore) {
        queryFilters.push({
            filterType: FILTER_TYPE.MUST,
            queryParams: [{
                field: QUERY_FIELD.CREATED_AT,
                queryType: QUERY_TYPE.LESS_THAN_EQUAL,
                value: [localEndOfDayIso(createdBefore)]
            }]
        });
    }

    return queryFilters;
}

/**
 * Full-screen Containers view opened from the WTN tab.
 *
 * Filters (left): status + created-date range — drive server-side queryFilters.
 * Results (right): server-paginated container cards. Each filter/page change
 * triggers a fresh `searchEWasteContainers` call.
 */
const ContainerView = ({open, onClose, intl}) => {
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const [containers, setContainers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const [pendingClose, setPendingClose] = useState(null);
    const [closing, setClosing] = useState(false);
    // Bumped after a successful close so the effect below refetches and the
    // closed container drops out of OPEN-filtered views.
    const [refetchTick, setRefetchTick] = useState(0);

    const [detailsContainer, setDetailsContainer] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [addItemsContainer, setAddItemsContainer] = useState(null);

    const siteName = getStationName();

    // Auto-dismiss success alert after 5 seconds
    useEffect(() => {
        if (!successMsg) return undefined;
        const timer = setTimeout(() => setSuccessMsg(""), 5000);
        return () => clearTimeout(timer);
    }, [successMsg]);

    useEffect(() => {
        if (!open || !siteName) {
            return undefined;
        }

        let ignore = false;
        setLoading(true);
        setErrorMsg("");

        const queryFilters = buildQueryFilters(filters);
        searchEWasteContainers(siteName, {
            from: page * pageSize,
            size: pageSize,
            queryFilters
        }).then((result) => {
            if (ignore) return;
            if (result.success) {
                setContainers(result.entities || []);
                setTotalCount(result.count || 0);
            } else {
                console.error("searchEWasteContainers failed", {
                    siteName,
                    from: page * pageSize,
                    size: pageSize,
                    queryFilters,
                    result
                });
                setContainers([]);
                setTotalCount(0);
                setErrorMsg(intl.formatMessage({
                    id: WTN_MSG.CONTAINER.ERROR.LOAD_FAILED,
                    defaultMessage: "Failed to load containers."
                }));
            }
            setLoading(false);
        });

        return () => { ignore = true; };
    }, [open, siteName, page, pageSize, filters, refetchTick, intl]);

    // Reset transient state when the modal closes so reopening feels fresh.
    useEffect(() => {
        if (!open) {
            setFilters(INITIAL_FILTERS);
            setPage(0);
            setPageSize(DEFAULT_PAGE_SIZE);
            setErrorMsg("");
            setSuccessMsg("");
            setPendingClose(null);
            setContainers([]);
            setTotalCount(0);
            setDetailsContainer(null);
            setCreateOpen(false);
            setAddItemsContainer(null);
        }
    }, [open]);

    const handleRequestClose = (container) => {
        setPendingClose(container);
    };

    const handleViewDetails = (container) => setDetailsContainer(container);

    const handleAddItems = (container) => {
        setSuccessMsg("");
        setAddItemsContainer(container);
    };

    const handleItemCreated = () => {
        const container = addItemsContainer;
        setAddItemsContainer(null);
        setRefetchTick((tick) => tick + 1);
        if (container) {
            const name = buildTitle(container) || container.containerDisplayId || "";
            setErrorMsg("");
            setSuccessMsg(intl.formatMessage(
                {
                    id: WTN_MSG.CONTAINER.SUCCESS.ITEM_ADDED,
                    defaultMessage: "Item added to {container} successfully."
                },
                {container: name}
            ));
        }
    };

    const handleAddItemsError = (msg) => {
        // Dismiss the add-item modal first so the inline alert in the
        // main view is visible; otherwise the modal hides it.
        setAddItemsContainer(null);
        setSuccessMsg("");
        setErrorMsg(msg);
    };

    const handleContainerCreated = (result) => {
        setCreateOpen(false);
        setRefetchTick((tick) => tick + 1);
        setErrorMsg("");
        const displayId = (result && result.containerDisplayId) || (result && result.containerId) || "";
        setSuccessMsg(intl.formatMessage(
            {
                id: WTN_MSG.CONTAINER.SUCCESS.CONTAINER_CREATED,
                defaultMessage: "Container {displayId} created successfully."
            },
            {displayId}
        ));
    };

    const handleCreateError = (msg) => {
        // Dismiss the create modal first so the inline alert in the
        // main view is visible; otherwise the modal hides it.
        setCreateOpen(false);
        setSuccessMsg("");
        setErrorMsg(msg);
    };

    const confirmClose = async () => {
        if (!pendingClose || !pendingClose.id) return;
        setClosing(true);
        try {
            // WADM expects locale with underscore (e.g., "en_GB", "de_DE").
            // Use the app's configured locale via react-intl (NOT navigator.language,
            // which reflects the browser and ignores in-app locale overrides).
            // Global regex handles multi-segment tags like "zh-Hans-CN" → "zh_Hans_CN".
            const locale = (intl.locale || "en-GB").replace(/-/g, "_");
            const result = await closeContainerApi(pendingClose.id, locale);
            if (result.success) {
                setPendingClose(null);
                setRefetchTick((tick) => tick + 1);
            } else {
                console.error("closeEWasteContainer failed", {
                    containerId: pendingClose.id,
                    result
                });
                // Dismiss the confirmation modal first so the error alert in
                // the main view is visible; otherwise the modal hides it.
                setPendingClose(null);
                setErrorMsg(intl.formatMessage({
                    id: WTN_MSG.CONTAINER.ERROR.CLOSE_FAILED,
                    defaultMessage: "Failed to close container."
                }));
            }
        } finally {
            setClosing(false);
        }
    };

    if (!open) return null;

    const pageCount = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
    const hasResults = containers.length > 0;
    const firstIndex = hasResults ? page * pageSize + 1 : 0;
    const lastIndex = hasResults ? page * pageSize + containers.length : 0;

    return (
        <Modal
            open={open}
            onClose={onClose}
            describe="Containers"
            width="95vw"
            scrollContainer="modal"
        >
            {/* Header */}
            <Row
                alignmentHorizontal="justify"
                alignmentVertical="center"
                spacingInset="medium large"
                backgroundColor={colorGray700}
            >
                <Text type="h200" color="inverted">
                    <FormattedMessage id={WTN_MSG.CONTAINER.HEADING} defaultMessage="Containers" />
                </Text>
                <Button type="icon" onClick={onClose}>
                    <Icon tokens={closeTokens}>
                        <FormattedMessage id={WTN_MSG.CONTAINER.CLOSE} defaultMessage="Close" />
                    </Icon>
                </Button>
            </Row>

            <Row alignmentHorizontal="start" alignmentVertical="top" widths={["300px", "fill"]} spacing="none">
                {/* Filter panel */}
                <Column
                    spacingInset="large medium"
                    spacing="medium"
                    backgroundColor="secondary"
                >
                    <Text type="h100">
                        <FormattedMessage id={WTN_MSG.CONTAINER.FILTERS} defaultMessage="FILTERS" />
                    </Text>

                    <Column spacing="small">
                        <Text type="b200">
                            <FormattedMessage
                                id={WTN_MSG.CONTAINER.FILTER.STATUS}
                                defaultMessage="Container Status"
                            />
                        </Text>
                        <Select
                            value={filters.status}
                            onChange={(value) => { setPage(0); setFilters((prev) => ({...prev, status: value})); }}
                            size="small"
                            width="100%"
                        >
                            <SelectOption
                                value={STATUS_FILTER.ALL}
                                label={intl.formatMessage({
                                    id: WTN_MSG.CONTAINER.FILTER.STATUS_ALL,
                                    defaultMessage: "All"
                                })}
                            />
                            <SelectOption
                                value={STATUS_FILTER.OPEN}
                                label={intl.formatMessage({
                                    id: WTN_MSG.CONTAINER.FILTER.STATUS_OPEN,
                                    defaultMessage: "Open"
                                })}
                            />
                            <SelectOption
                                value={STATUS_FILTER.CLOSED}
                                label={intl.formatMessage({
                                    id: WTN_MSG.CONTAINER.FILTER.STATUS_CLOSED,
                                    defaultMessage: "Closed"
                                })}
                            />
                        </Select>
                    </Column>

                    <Column spacing="small">
                        <Text type="b200">
                            <FormattedMessage
                                id={WTN_MSG.CONTAINER.FILTER.CREATED_DATE}
                                defaultMessage="Created date"
                            />
                        </Text>
                        <Row spacing="small">
                            <DatePicker
                                value={filters.createdAfter}
                                onChange={(value) => { setPage(0); setFilters((prev) => ({...prev, createdAfter: value})); }}
                                size="small"
                            />
                            <DatePicker
                                value={filters.createdBefore}
                                onChange={(value) => { setPage(0); setFilters((prev) => ({...prev, createdBefore: value})); }}
                                size="small"
                            />
                        </Row>
                    </Column>
                </Column>

                {/* Results panel */}
                <Column spacingInset="large" spacing="medium">
                    <Row alignmentHorizontal="justify" alignmentVertical="center">
                        <Column spacing="none">
                            <Text type="h200">
                                {hasResults ? (
                                    <FormattedMessage
                                        id={WTN_MSG.CONTAINER.SHOWING}
                                        defaultMessage="Showing {from}-{to} of {total}"
                                        values={{from: firstIndex, to: lastIndex, total: totalCount}}
                                    />
                                ) : (
                                    <FormattedMessage
                                        id={WTN_MSG.CONTAINER.RESULTS}
                                        defaultMessage="{filtered} / {total} results"
                                        values={{filtered: 0, total: totalCount}}
                                    />
                                )}
                            </Text>
                        </Column>
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => {
                                setSuccessMsg("");
                                setCreateOpen(true);
                            }}
                        >
                            <Icon tokens={plusTokens} />
                            <FormattedMessage
                                id={WTN_MSG.CONTAINER.CREATE_CONTAINER}
                                defaultMessage="Create container"
                            />
                        </Button>
                    </Row>

                    {errorMsg ? (
                        <Alert type="error">{errorMsg}</Alert>
                    ) : null}

                    {successMsg ? (
                        <Alert type="success" onClose={() => setSuccessMsg("")}>{successMsg}</Alert>
                    ) : null}

                    {loading ? (
                        <Row alignmentHorizontal="center" spacingInset="large">
                            <Loader />
                        </Row>
                    ) : !hasResults ? (
                        <Box type="outline" spacingInset="large">
                            <Text type="b200" color="secondary" alignment="center">
                                <FormattedMessage
                                    id={WTN_MSG.CONTAINER.NO_RESULTS}
                                    defaultMessage="No containers found"
                                />
                            </Text>
                        </Box>
                    ) : (
                        <Column spacing="medium">
                            {containers.map((container) => (
                                <ContainerCard
                                    key={container.id || container.containerDisplayId}
                                    container={container}
                                    onPrintLabel={printContainerLabel}
                                    onRequestClose={handleRequestClose}
                                    onViewDetails={handleViewDetails}
                                    onAddItems={handleAddItems}
                                />
                            ))}
                        </Column>
                    )}

                    {/* Pagination */}
                    {totalCount > 0 && (
                        <Row alignmentHorizontal="justify" alignmentVertical="center">
                            <Select
                                value={pageSize}
                                onChange={(value) => { setPageSize(value); setPage(0); }}
                                width={140}
                                size="small"
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <SelectOption
                                        key={size}
                                        value={size}
                                        label={intl.formatMessage(
                                            {id: WTN_MSG.PER_PAGE, defaultMessage: "{size} per page"},
                                            {size}
                                        )}
                                    />
                                ))}
                            </Select>
                            <Row spacing="small">
                                <Button
                                    type="secondary"
                                    size="small"
                                    disabled={page === 0 || loading}
                                    onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                                >
                                    <FormattedMessage id={WTN_MSG.PREVIOUS} defaultMessage="< Previous" />
                                </Button>
                                <Button
                                    type="secondary"
                                    size="small"
                                    disabled={page >= pageCount - 1 || loading}
                                    onClick={() => setPage((prev) => prev + 1)}
                                >
                                    <FormattedMessage id={WTN_MSG.NEXT} defaultMessage="Next >" />
                                </Button>
                            </Row>
                        </Row>
                    )}
                </Column>
            </Row>

            <ContainerDetailsModal
                container={detailsContainer}
                open={!!detailsContainer}
                onClose={() => setDetailsContainer(null)}
            />

            <CreateContainerModal
                open={createOpen}
                siteName={siteName}
                onCreated={handleContainerCreated}
                onCancel={() => setCreateOpen(false)}
                onError={handleCreateError}
            />

            <CreateItemModal
                open={!!addItemsContainer}
                container={addItemsContainer}
                onCreated={handleItemCreated}
                onCancel={() => setAddItemsContainer(null)}
                onError={handleAddItemsError}
            />

            {/* Close-confirmation dialog */}
            {pendingClose && (
                <Modal
                    open={!!pendingClose}
                    onClose={() => !closing && setPendingClose(null)}
                    title={
                        <FormattedMessage
                            id={WTN_MSG.CONTAINER.CLOSE_CONFIRM.TITLE}
                            defaultMessage="Close container?"
                        />
                    }
                    describe="Close container confirmation"
                >
                    <Text type="b200">
                        <FormattedMessage
                            id={WTN_MSG.CONTAINER.CLOSE_CONFIRM.BODY}
                            defaultMessage="Closing {name} will stop new items from being added. This cannot be undone."
                            values={{
                                name: pendingClose.containerDisplayName
                                    || pendingClose.containerDisplayId
                                    || ""
                            }}
                        />
                    </Text>
                    <ModalFooter>
                        <Row alignmentHorizontal="end" spacing="small">
                            <Button
                                type="secondary"
                                size="small"
                                disabled={closing}
                                onClick={() => setPendingClose(null)}
                            >
                                <FormattedMessage id={WTN_MSG.CONTAINER.CLOSE_CONFIRM.CANCEL} defaultMessage="Cancel" />
                            </Button>
                            <Button
                                type="primary"
                                size="small"
                                disabled={closing}
                                onClick={confirmClose}
                            >
                                <FormattedMessage id={WTN_MSG.CONTAINER.CLOSE_CONFIRM.CONFIRM} defaultMessage="Close container" />
                            </Button>
                        </Row>
                    </ModalFooter>
                </Modal>
            )}
        </Modal>
    );
};

export default injectIntl(ContainerView);



// WEBPACK FOOTER //
// ./src/components/wtn/ContainerView.js